<script setup>
import {
	ref,
	computed,
	onBeforeMount,
	onMounted,
	onBeforeUnmount,
	watch,
} from "vue";
import { Report } from "@/report";
import { Utils } from "@/utils.js";
import { CanvasUtils } from "@/utils-canvas.js";
import { useMainStore } from "@/stores/store.js";

const store = useMainStore();

const MM_TO_FEET_CONVERSION = 0.00328084;
const isLoading = ref(false);
const readyToCalc = ref(false);
const canvas = ref(null);
const canvasContainer = ref(null);
const padding = ref(10); // Outer padding around the room layout inside the canvas
const drawableWidth = ref(0);
const drawableHeight = ref(0);
const gridStep = ref(20);
const dotRadius = ref(1);
const furniture = ref({ selected: "" });
const numProductImages = ref(1);
const productPositions = ref([]);
const checkCalcReadyInterval = ref(null); // Store interval reference at component level
const productTooBigForRoom = ref(false);
const isCalculating = ref(false); // Flag to prevent recursive calls
const isGeneratingReport = ref(false); // Flag to prevent duplicate report generation
const isExporting = ref(false); // Flag to indicate export mode (shows all products, draws canvas legend)
const legendHeight = ref(0); // Height of the legend area at the bottom of the canvas

// Product SVG stroke width for canvas rendering
const PRODUCT_STROKE_WIDTH = "5px";

// Gap between wall-mounted panels in millimeters (for visual separation on canvas)
const WALL_MOUNTED_GAP_MM = 15;

// When true, panels are evenly distributed around the room perimeter.
// When false, panels are placed side-by-side starting from the top wall.
const DISTRIBUTE_WALL_PANELS = false;

// Cache for shape-product SVG viewBox dimensions and raw text
// Key: "TE_L_2" → { width, height, svgText }
const svgDimensionCache = new Map();

// Legend configuration constants
const LEGEND_CONFIG = {
	itemHeight: 38, // Height of each legend item row (includes line spacing)
	itemGap: 16, // Horizontal gap between legend items
	swatchSize: 16, // Size of the color swatch
	swatchGap: 8, // Gap between swatch and text
	fontSize: 12, // Font size for legend text
	paddingTop: 20, // Padding above legend (space between room and legend)
	paddingBottom: 24, // Padding below legend (space between legend and canvas edge)
	minItemWidth: 150, // Minimum width for each legend item
};

// Define strong, distinct colors for different product groups (cycle through if more products than colors)
const PRODUCT_COLORS = [
	"#DC2626", // Red-600
	"#2563EB", // Blue-600
	"#059669", // Emerald-600
	"#7C3AED", // Violet-600
	"#D97706", // Amber-600
	"#DB2777", // Pink-600
	"#0891B2", // Cyan-600
	"#4F46E5", // Indigo-600
	"#65A30D", // Lime-600
	"#EA580C", // Orange-600
];

// Wall-mounted products that render as wall segments (eg. Motif Panels)
const WALL_MOUNTED_PRODUCT_IDS = ["MB", "MP", "MR", "MS", "MV", "MW"];

// Check if any product is a wall-mounted product
const hasWallMountedProducts = () => {
	return store.user.products.some((product) =>
		WALL_MOUNTED_PRODUCT_IDS.includes(product.data?.id),
	);
};

// Get wall-mounted product(s) from the products array
const getWallMountedProducts = () => {
	return store.user.products.filter((product) =>
		WALL_MOUNTED_PRODUCT_IDS.includes(product.data?.id),
	);
};

// Track visibility state for each product (keyed by product.id)
const productVisibility = ref({});

// Computed property for product color legend
// IMPORTANT: This must mirror the exact same color assignment logic as drawRoom() and drawProductImages()
const productColorLegend = computed(() => {
	const legend = [];
	let colorIndex = 0;

	for (const product of store.user.products) {
		// Only include products with valid data and size
		if (!product.data || !product.size) {
			continue;
		}

		const isWallMounted = WALL_MOUNTED_PRODUCT_IDS.includes(product.data?.id);

		// For non-wall-mounted products, check for valid size data (must match drawProductImages)
		if (!isWallMounted) {
			const sizeData = product.data.sizes?.items?.[product.size];
			if (!sizeData || !sizeData.id) {
				continue;
			}
		}

		const productName = product.data.name || product.product;
		const sizeLabel = isWallMounted
			? product.size_variant || product.size
			: product.data.sizes?.items?.[product.size]?.name || product.size;
		const color = PRODUCT_COLORS[colorIndex % PRODUCT_COLORS.length];

		// Initialize visibility state if not set (default to visible)
		if (productVisibility.value[product.id] === undefined) {
			productVisibility.value[product.id] = true;
		}

		// Check if product is non-lit:
		// 1. selectedLumenOutput is "NON-LIT" (lit product with non-lit option selected)
		// 2. No dataExtended (product doesn't have lighting options)
		const isNonLit =
			product.selectedLumenOutput === "NON-LIT" ||
			!product.dataExtended ||
			product.dataExtended.length === 0;

		// Get selected lumen output for lit products (e.g., "D", "I", etc.)
		const lumenOutput =
			!isNonLit && product.selectedLumenOutput
				? product.selectedLumenOutput
				: null;

		legend.push({
			productId: product.id,
			color,
			productName,
			sizeLabel,
			variantLabel: isWallMounted ? null : product.size_variant || null,
			visible: productVisibility.value[product.id],
			isNonLit,
			lumenOutput,
		});

		colorIndex++;
	}

	return legend;
});

/*
------------------------------------------------------------------------
Get count of currently visible products
------------------------------------------------------------------------
*/
const getVisibleProductCount = () => {
	return productColorLegend.value.filter((item) => item.visible).length;
};

/*
------------------------------------------------------------------------
Check if all products are currently visible
------------------------------------------------------------------------
*/
const allProductsVisible = computed(() => {
	const legend = productColorLegend.value;
	return legend.length > 0 && legend.every((item) => item.visible);
});

/*
------------------------------------------------------------------------
Check if a product can be toggled off (at least one must remain visible)
------------------------------------------------------------------------
*/
const canToggleOff = (productId) => {
	// If product is already hidden, it can always be toggled on
	if (!isProductVisible(productId)) {
		return true;
	}
	// If this is the last visible product, it cannot be toggled off
	return getVisibleProductCount() > 1;
};

/*
------------------------------------------------------------------------
Toggle visibility of a product in the canvas mockup
At least one product must always remain visible.
------------------------------------------------------------------------
*/
const toggleProductVisibility = (productId) => {
	const isCurrentlyVisible = isProductVisible(productId);

	// Prevent toggling off the last visible product
	if (isCurrentlyVisible && !canToggleOff(productId)) {
		return;
	}

	productVisibility.value[productId] = !productVisibility.value[productId];
	// Trigger canvas redraw without loading overlay or recalculation
	redrawCanvasOnly();
};

/*
------------------------------------------------------------------------
Show all products in the canvas mockup
------------------------------------------------------------------------
*/
const showAllProducts = () => {
	for (const item of productColorLegend.value) {
		productVisibility.value[item.productId] = true;
	}
	redrawCanvasOnly();
};

/*
------------------------------------------------------------------------
Check if a product is visible (for drawing functions)
------------------------------------------------------------------------
*/
const isProductVisible = (productId) => {
	return productVisibility.value[productId] !== false;
};

/*
------------------------------------------------------------------------
Calculate the height needed for the legend based on available width
Returns the height in pixels that the legend area needs
------------------------------------------------------------------------
*/
const calculateLegendHeight = (availableWidth) => {
	const legend = productColorLegend.value;
	if (legend.length === 0) {
		return 0;
	}

	// Create a temporary canvas to measure text widths
	const tempCanvas = document.createElement("canvas");
	const tempCtx = tempCanvas.getContext("2d");
	tempCtx.font = `${LEGEND_CONFIG.fontSize}px sans-serif`;

	// Calculate width of each legend item
	const itemWidths = legend.map((item) => {
		let text = `${item.productName} (${item.sizeLabel}`;
		if (item.variantLabel) {
			text += ` — ${item.variantLabel}`;
		}
		if (item.isNonLit) {
			text += " — non-lit";
		}
		text += ")";
		const textWidth = tempCtx.measureText(text).width;
		return Math.max(
			LEGEND_CONFIG.minItemWidth,
			LEGEND_CONFIG.swatchSize +
				LEGEND_CONFIG.swatchGap +
				textWidth +
				LEGEND_CONFIG.itemGap,
		);
	});

	// Calculate how many items fit per row
	let currentRowWidth = 0;
	let numRows = 1;
	const usableWidth = availableWidth - 2 * padding.value;

	itemWidths.forEach((itemWidth) => {
		if (currentRowWidth + itemWidth > usableWidth && currentRowWidth > 0) {
			numRows++;
			currentRowWidth = itemWidth;
		} else {
			currentRowWidth += itemWidth;
		}
	});

	// Calculate total height
	const totalHeight =
		LEGEND_CONFIG.paddingTop +
		numRows * LEGEND_CONFIG.itemHeight +
		LEGEND_CONFIG.paddingBottom;

	return totalHeight;
};

/*
------------------------------------------------------------------------
Draw the legend on the canvas at the specified Y position
------------------------------------------------------------------------
*/
const drawLegend = (ctx, startY, canvasWidth) => {
	const legend = productColorLegend.value;
	if (legend.length === 0) {
		return;
	}

	// Set up text rendering
	ctx.font = `${LEGEND_CONFIG.fontSize}px sans-serif`;
	ctx.textBaseline = "middle";

	// Calculate item widths
	const itemWidths = legend.map((item) => {
		let text = `${item.productName} (${item.sizeLabel}`;
		if (item.variantLabel) {
			text += ` — ${item.variantLabel}`;
		}
		if (item.isNonLit) {
			text += " — non-lit";
		}
		text += ")";
		const textWidth = ctx.measureText(text).width;
		return {
			width: Math.max(
				LEGEND_CONFIG.minItemWidth,
				LEGEND_CONFIG.swatchSize +
					LEGEND_CONFIG.swatchGap +
					textWidth +
					LEGEND_CONFIG.itemGap,
			),
			text,
			color: item.color,
		};
	});

	// Layout items in rows
	const usableWidth = canvasWidth - 2 * padding.value;
	const rows = [];
	let currentRow = [];
	let currentRowWidth = 0;

	itemWidths.forEach((item) => {
		if (currentRowWidth + item.width > usableWidth && currentRow.length > 0) {
			rows.push({ items: currentRow, width: currentRowWidth });
			currentRow = [item];
			currentRowWidth = item.width;
		} else {
			currentRow.push(item);
			currentRowWidth += item.width;
		}
	});
	if (currentRow.length > 0) {
		rows.push({ items: currentRow, width: currentRowWidth });
	}

	// Draw each row, left-aligned
	let y = startY + LEGEND_CONFIG.paddingTop + LEGEND_CONFIG.itemHeight / 2;

	rows.forEach((row) => {
		// Left-align the row
		let x = padding.value;

		row.items.forEach((item) => {
			// Draw color swatch with border
			ctx.fillStyle = item.color;
			ctx.fillRect(
				x,
				y - LEGEND_CONFIG.swatchSize / 2,
				LEGEND_CONFIG.swatchSize,
				LEGEND_CONFIG.swatchSize,
			);
			ctx.strokeStyle = "rgba(0, 0, 0, 0.2)";
			ctx.lineWidth = 1;
			ctx.strokeRect(
				x,
				y - LEGEND_CONFIG.swatchSize / 2,
				LEGEND_CONFIG.swatchSize,
				LEGEND_CONFIG.swatchSize,
			);

			// Draw text
			ctx.fillStyle = "#374151"; // gray-700
			ctx.fillText(
				item.text,
				x + LEGEND_CONFIG.swatchSize + LEGEND_CONFIG.swatchGap,
				y,
			);

			x += item.width;
		});

		y += LEGEND_CONFIG.itemHeight;
	});
};

/*
------------------------------------------------------------------------
Actions to perform before mount of the component.
------------------------------------------------------------------------
*/
onBeforeMount(() => {
	// store.overlays.loading = true;
});

/*
------------------------------------------------------------------------
Actions on mount of the component.
- Set up debounce to control rerender image.
- Watch for store changes a render image.
- Window resize listener to trigger image render.
------------------------------------------------------------------------
*/
let debouncedUpdate;

onMounted(() => {
	debouncedUpdate = Utils.debounce(() => {
		resizeCanvas();
	}, 200);

	// Watch for changes that should trigger canvas resize
	watch(
		() => [
			store.user.room.length,
			store.user.room.width,
			store.user.room.height,
			store.user.room.unit,
			store.user.room.materials,
			store.user.room.usage,
		],
		() => {
			// Skip if calculation is in progress to prevent recursion
			if (store.isCalculating || isCalculating.value) {
				return;
			}
			debouncedUpdate();
		},
	);

	// Watch product changes separately to avoid recursive loops
	watch(
		() => {
			// Watch the active product ID to detect product changes
			return store.user.activeProductId;
		},
		(newId, oldId) => {
			// Skip if calculation is in progress to prevent recursion
			if (store.isCalculating || isCalculating.value) {
				return;
			}
			// Only trigger if product actually changed
			if (newId !== oldId) {
				debouncedUpdate();
			}
		},
	);

	// Watch all products' size, variant, and lumen output changes (not metric_sabins - that's a calculation result)
	//
	// RECURSION PREVENTION WATCHER #1: Product sizes, variants, and lumen outputs
	// Monitors user changes to product sizes/variants/lumen outputs. When calculateProductsBatch() updates
	// product.calculation, Vue sees the product object as changed and could trigger this watcher.
	// We check isCalculating flags in BOTH source function and callback to prevent recursion.
	//
	watch(
		() => {
			if (!store.user.products || store.user.products.length === 0) return "";
			// RECURSION PREVENTION: Return empty string if calculating to prevent callback
			if (store.isCalculating || isCalculating.value) {
				return "";
			}
			return store.user.products
				.map(
					(p) =>
						`${p.id}:${p.size || ""}-${p.size_variant || ""}-${p.selectedLumenOutput || ""}`,
				)
				.join("|");
		},
		(newVal, oldVal) => {
			// RECURSION PREVENTION: Double-safety check in callback
			// Critical because flush:'post' runs after DOM updates, potentially after flag is cleared
			if (store.isCalculating || isCalculating.value) {
				return;
			}
			if (newVal !== oldVal && newVal !== "" && oldVal !== "") {
				debouncedUpdate();
			}
		},
		{ flush: "post" }, // Delays callback until after DOM updates, giving calculateProductsBatch time to set flag
	);

	// Watch all product manual quantities (only manualUnits, not calculatedUnits to avoid recursion)
	//
	// RECURSION PREVENTION WATCHER #2: Product manual units
	// Monitors user changes to manualUnits. IMPORTANT: We only watch manualUnits, NOT calculatedUnits,
	// because calculatedUnits is updated by calculateProductsBatch(), which would cause recursion:
	//   calculateProductsBatch() -> updates calculatedUnits -> watcher fires -> resizeCanvas() -> loop
	//
	watch(
		() => {
			if (!store.user.products || store.user.products.length === 0) return "";
			// RECURSION PREVENTION: Return empty string if calculating to prevent callback
			if (store.isCalculating || isCalculating.value) {
				return "";
			}
			return store.user.products
				.map((p) => {
					// RECURSION PREVENTION: Only watch manualUnits, NOT calculatedUnits
					// calculatedUnits is updated by calculateProductsBatch() and watching it causes recursion
					const quantity = p.manualUnits ?? null;
					return `${p.id}:${quantity}`;
				})
				.join("|");
		},
		(newVal, oldVal) => {
			// RECURSION PREVENTION: Double-safety check in callback
			// Critical because flush:'post' runs after DOM updates, potentially after flag is cleared
			if (store.isCalculating || isCalculating.value) {
				return;
			}
			if (newVal !== oldVal && newVal !== "" && oldVal !== "") {
				debouncedUpdate();
			}
		},
		{ flush: "post" }, // Delays callback until after DOM updates, giving calculateProductsBatch time to set flag
	);

	// Watch products array length to catch additions/removals
	//
	// NOTE: No isCalculating check here because calculateProductsBatch() never changes
	// the products array length - it only updates product properties. Array length changes
	// only from addProduct/removeProduct which should always trigger a canvas update.
	//
	watch(
		() => {
			return store.user.products?.length || 0;
		},
		(newVal, oldVal) => {
			if (newVal !== oldVal) {
				debouncedUpdate();
			}
		},
	);

	//
	// Listen for trigger to generate the report.
	// Guard against duplicate triggers using isGeneratingReport flag.
	//
	watch(
		() => store.triggerDocGen,
		(newValue, oldValue) => {
			if (newValue === true && !isGeneratingReport.value) {
				// Immediately reset trigger to prevent duplicate watch fires
				store.triggerDocGen = false;
				triggerReportGenerate();
			}
		},
	);

	//
	// Watch for canvas redraw signal from store
	// This is set after calculateProductsBatch() completes, ensuring the canvas
	// redraws after product additions/removals/resets are fully processed.
	// Uses redrawCanvasOnly() instead of resizeCanvas() since calculation is already done.
	//
	watch(
		() => store.canvasNeedsRedraw,
		(needsRedraw) => {
			if (needsRedraw) {
				$log.log("[RoomCanvas:watch] Canvas redraw triggered by store flag");
				store.canvasNeedsRedraw = false; // Reset the flag
				redrawCanvasOnly();
			}
		},
	);

	debouncedUpdate();

	//
	// Add resize event listener
	//
	let cachedWidth = window.innerWidth;
	const handleResize = () => {
		const newWidth = window.innerWidth;
		if (newWidth !== cachedWidth) {
			cachedWidth = newWidth;
			debouncedUpdate(); // Call the debounced update function
		}
	};

	window.addEventListener("resize", handleResize);

	//
	// Clean up the event listener and interval on component unmount
	//
	onBeforeUnmount(() => {
		window.removeEventListener("resize", handleResize);
		window.removeEventListener("resize", debouncedUpdate);
		if (checkCalcReadyInterval.value !== null) {
			clearInterval(checkCalcReadyInterval.value);
			checkCalcReadyInterval.value = null;
		}
	});
});

/*
------------------------------------------------------------------------
Start the report generation process.
Uses isGeneratingReport guard to prevent duplicate executions.
------------------------------------------------------------------------
*/
const triggerReportGenerate = async () => {
	// Guard against duplicate execution
	if (isGeneratingReport.value) {
		$log.warn(
			"[RoomCanvas:triggerReportGenerate] Report generation already in progress, ignoring duplicate trigger",
		);
		return;
	}

	isGeneratingReport.value = true;
	store.overlays.loading = true;
	$log.log(`[RoomCanvas:triggerReportGenerate] Starting report generation`);

	try {
		await exportCanvasAsImage();
		// Kick off the report document generation process.
		await Report.generateReport(store);
	} finally {
		isGeneratingReport.value = false;
	}
};

/*
------------------------------------------------------------------------
Primary entry point to render the canvas

RECURSION PREVENTION:
This function can be triggered by watchers when product data changes. When calculateProductsBatch()
updates product.calculation, it triggers Vue reactivity which can cause watchers to fire,
creating a loop: resizeCanvas() -> checkReadyToCalc() -> calculateProductsBatch() -> watchers fire -> ...

Prevention strategy:
- Guards check isCalculating flags before proceeding (this function, checkReadyToCalc, watchers)
- calculateProductsBatch() sets isCalculating flag before updates and delays clearing by 500ms
- Watchers check flags in both source functions and callbacks
- Only update calculations if they changed to minimize reactivity triggers
------------------------------------------------------------------------
*/
const resizeCanvas = async () => {
	// Do not run if document is generating or has been generated.
	if (store.triggerDocGen || store.user.reportGenerated) {
		return false;
	}

	// RECURSION PREVENTION: Check flags before proceeding to prevent running during calculation
	// Flag is set in calculateProductsBatch() before store updates and cleared after 500ms delay
	if (isCalculating.value || store.isCalculating) {
		return false;
	}

	// isLoading.value = true;

	// RECURSION PREVENTION: Clear any existing interval to prevent multiple simultaneous calls
	if (checkCalcReadyInterval.value !== null) {
		clearInterval(checkCalcReadyInterval.value);
		checkCalcReadyInterval.value = null;
	}

	//
	// Make sure all required data has been selected before showing loader and rerendering the canvas.
	// Check both product size validation AND report validation (size, variant, lumen output)
	//
	if (store.validateProductSize && store.stepReportIsValid) {
		$log.log("[RoomCanvas:resizeCanvas] Setting loading overlay to true");
		store.overlays.loading = true;
	} else {
		// Clear overlay if validation fails - prevents overlay from staying stuck
		// when App.vue sets it to true during initialization but products aren't ready
		$log.log(
			"[RoomCanvas:resizeCanvas] Validation failed - clearing loading overlay",
		);
		store.overlays.loading = false;
		return false;
	}

	//
	// Use setInterval to call checkReadyToCalc every 1000ms
	// Checks if products have metricSabins data, and if so, triggers calculation
	//
	const checkReadyToCalc = async () => {
		// RECURSION PREVENTION: Check flags before proceeding
		// Critical because interval might fire multiple times before calculation completes
		if (isCalculating.value || store.isCalculating) {
			return;
		}

		const currentProduct = store.currentProduct;
		readyToCalc.value =
			currentProduct &&
			currentProduct.metricSabins !== undefined &&
			currentProduct.metricSabins !== "";
		if (readyToCalc.value) {
			// RECURSION PREVENTION: Clear interval first to prevent race conditions
			clearInterval(checkCalcReadyInterval.value);
			checkCalcReadyInterval.value = null;

			// RECURSION PREVENTION: Set local flag before calling calculateProductsBatch
			// Works with store flag (set in calculateProductsBatch) to prevent recursive calls
			isCalculating.value = true;
			let result;
			try {
				// calculateProductsBatch() sets store.isCalculating and delays clearing by 500ms
				// to let Vue reactivity settle before watchers can fire again
				result = await store.calculateProductsBatch();
			} catch (error) {
				throw error;
			} finally {
				// Clear local flag. Store flag cleared in calculateProductsBatch() after delay
				isCalculating.value = false;
			}

			if (result) {
				$log.log(
					"[RoomCanvas:resizeCanvas] Batch calculation completed successfully",
				);
				isLoading.value = false;

				// Calculate total number of product images across ALL products
				// Exclude wall-mounted products as they are rendered as wall segments, not product images
				let totalUnits = 0;
				store.user.products.forEach((product) => {
					// Skip wall-mounted products - they don't use product images
					if (WALL_MOUNTED_PRODUCT_IDS.includes(product.data?.id)) {
						return;
					}
					// Use manualUnits if set, otherwise use calculated units
					const quantity =
						product.manualUnits ?? product.calculation?.calculatedUnits ?? 1;
					totalUnits += quantity;
				});
				numProductImages.value = totalUnits;
				$log.log(
					"[RoomCanvas:resizeCanvas] Updated total numProductImages across all products to:",
					totalUnits,
				);

				// Always assign the longest dimension to roomWidth (x-axis)
				let roomWidth = Math.max(store.user.room.width, store.user.room.length);
				let roomLength = Math.min(
					store.user.room.width,
					store.user.room.length,
				);

				// Main canvas
				const ctx = canvas.value.getContext("2d");
				const containerWidth = canvasContainer.value.clientWidth;

				// Calculate grid step to fit the full container width for the room width
				gridStep.value = (containerWidth - 2 * padding.value) / roomWidth;
				// Calculate the drawable width and height based on the grid step and room dimensions
				drawableWidth.value = roomWidth * gridStep.value;
				drawableHeight.value = roomLength * gridStep.value;

				// Calculate legend height based on canvas width
				// Only include legend space in canvas when exporting (HTML legend used in normal mode)
				const canvasContentWidth = drawableWidth.value + 2 * padding.value;
				legendHeight.value = isExporting.value
					? calculateLegendHeight(canvasContentWidth)
					: 0;

				// Set high-resolution scaling factor (e.g., 2 for 2x resolution)
				const scale = window.devicePixelRatio || 2;
				// Set canvas dimensions, scaled up for high resolution (including legend height)
				canvas.value.width = canvasContentWidth * scale;
				canvas.value.height =
					(drawableHeight.value + 2 * padding.value + legendHeight.value) *
					scale;
				// Style canvas to display at actual size
				canvas.value.style.width = `${canvasContentWidth}px`;
				canvas.value.style.height = `${drawableHeight.value + 2 * padding.value + legendHeight.value}px`;
				// Scale the drawing context to match the scaling factor
				ctx.scale(scale, scale);

				// Draw the canvas contents at the new resolution
				drawCanvas(
					ctx,
					canvasContentWidth,
					drawableHeight.value + 2 * padding.value + legendHeight.value,
				);

				// isLoading.value = false;
				$log.log(
					"[RoomCanvas:resizeCanvas] Setting loading overlay to false - canvas drawn",
				);
				store.overlays.loading = false;

				// // Scroll to a room mockup
				// const scrollToElm = document.getElementById('room_mockup');
				// if (scrollToElm) {
				//     const offsetTop = scrollToElm.offsetTop; // Get the offset from the top of the document
				//     window.scrollTo({
				//         top: offsetTop,
				//         behavior: 'smooth', // Use smooth scrolling
				//     });
				// }
			}
		}
	};

	// Start the interval to check if data is ready to calc.
	checkCalcReadyInterval.value = setInterval(checkReadyToCalc, 1000);
};

/*
------------------------------------------------------------------------
Redraw the canvas without recalculating.

This function is used when calculation has already completed (e.g., after
calculateProductsBatch() sets canvasNeedsRedraw flag). It skips the
validation checks and recalculation, and directly redraws using existing
calculation data.
------------------------------------------------------------------------
*/
const redrawCanvasOnly = () => {
	$log.log("[RoomCanvas:redrawCanvasOnly] Drawing canvas with existing data");

	// Check if canvas elements are available
	if (!canvas.value || !canvasContainer.value) {
		$log.log("[RoomCanvas:redrawCanvasOnly] Canvas elements not available");
		return;
	}

	// Check if we have products with calculation data
	if (!store.user.products || store.user.products.length === 0) {
		$log.log("[RoomCanvas:redrawCanvasOnly] No products available");
		return;
	}

	// Calculate total number of product images across ALL products
	// Exclude wall-mounted products as they are rendered as wall segments, not product images
	let totalUnits = 0;
	store.user.products.forEach((product) => {
		// Skip wall-mounted products - they don't use product images
		if (WALL_MOUNTED_PRODUCT_IDS.includes(product.data?.id)) {
			return;
		}
		// Use manualUnits if set, otherwise use calculated units
		const quantity =
			product.manualUnits ?? product.calculation?.calculatedUnits ?? 1;
		totalUnits += quantity;
	});
	numProductImages.value = totalUnits;
	$log.log(
		"[RoomCanvas:redrawCanvasOnly] Updated numProductImages to:",
		totalUnits,
	);

	// Always assign the longest dimension to roomWidth (x-axis)
	let roomWidth = Math.max(store.user.room.width, store.user.room.length);
	let roomLength = Math.min(store.user.room.width, store.user.room.length);

	// Main canvas
	const ctx = canvas.value.getContext("2d");
	const containerWidth = canvasContainer.value.clientWidth;

	// Calculate grid step to fit the full container width for the room width
	gridStep.value = (containerWidth - 2 * padding.value) / roomWidth;
	// Calculate the drawable width and height based on the grid step and room dimensions
	drawableWidth.value = roomWidth * gridStep.value;
	drawableHeight.value = roomLength * gridStep.value;

	// Calculate legend height based on canvas width
	// Only include legend space in canvas when exporting (HTML legend used in normal mode)
	const canvasContentWidth = drawableWidth.value + 2 * padding.value;
	legendHeight.value = isExporting.value
		? calculateLegendHeight(canvasContentWidth)
		: 0;

	// Set high-resolution scaling factor (e.g., 2 for 2x resolution)
	const scale = window.devicePixelRatio || 2;
	// Set canvas dimensions, scaled up for high resolution (including legend height when exporting)
	canvas.value.width = canvasContentWidth * scale;
	canvas.value.height =
		(drawableHeight.value + 2 * padding.value + legendHeight.value) * scale;
	// Style canvas to display at actual size
	canvas.value.style.width = `${canvasContentWidth}px`;
	canvas.value.style.height = `${drawableHeight.value + 2 * padding.value + legendHeight.value}px`;
	// Scale the drawing context to match the scaling factor
	ctx.scale(scale, scale);

	// Draw the canvas contents at the new resolution
	drawCanvas(
		ctx,
		canvasContentWidth,
		drawableHeight.value + 2 * padding.value + legendHeight.value,
	);

	$log.log("[RoomCanvas:redrawCanvasOnly] Canvas redrawn successfully");
};

/*
------------------------------------------------------------------------
Draw the main canvas layout.
------------------------------------------------------------------------
*/
const drawCanvas = (ctx, width, height) => {
	// Always assign the longest dimension to roomWidth (x-axis)
	const roomWidth = Math.max(store.user.room.width, store.user.room.length);
	const roomLength = Math.min(store.user.room.width, store.user.room.length);

	// Clear canvas
	ctx.clearRect(0, 0, width, height);

	// Draw the background of the room rectangle
	ctx.fillStyle = "#ffffff"; // Set the desired fill color for the room area
	ctx.fillRect(
		padding.value,
		padding.value,
		drawableWidth.value,
		drawableHeight.value,
	);

	// Draw the room border
	ctx.strokeStyle = "black";
	ctx.lineWidth = 2; // Thinner room border
	ctx.strokeRect(
		padding.value,
		padding.value,
		drawableWidth.value,
		drawableHeight.value,
	);

	// When wall-mounted product exists, calculate the total width of all suggested panels.
	// Skip drawing if all wall-mounted products are hidden (unless exporting)
	if (hasWallMountedProducts()) {
		// Get all wall-mounted products and calculate total width
		const wallMountedProducts = getWallMountedProducts();

		// Check if any wall-mounted product is visible (or if exporting - show all)
		const anyWallMountedVisible =
			isExporting.value ||
			wallMountedProducts.some((p) => isProductVisible(p.id));
		if (!anyWallMountedVisible) {
			// Skip drawing wall-mounted product segments
		} else {
			// Build array of individual panels with their widths and colors
			// Each panel tracks: width in mm, color for that product
			const panels = [];

			// Calculate color index for each product based on its position in the products array
			// This ensures consistent colors between the canvas and the legend
			let colorIndex = 0;
			for (const product of store.user.products) {
				const isWallMounted = WALL_MOUNTED_PRODUCT_IDS.includes(
					product.data?.id,
				);

				// Skip products without valid data or size
				if (!product.data || !product.size) {
					continue;
				}

				// For wall-mounted products: add panels with the correct color
				if (isWallMounted) {
					// Skip hidden products (unless exporting)
					if (!isExporting.value && !isProductVisible(product.id)) {
						colorIndex++;
						continue;
					}

					const color = PRODUCT_COLORS[colorIndex % PRODUCT_COLORS.length];
					const wmQuantity =
						product.manualUnits ?? product.calculation?.unitsUsed ?? 1;
					const sizeData = product.data?.sizes?.items?.[product.size];
					const wmWidth = parseInt(sizeData?.w, 10) || 0;

					for (let i = 0; i < wmQuantity; i++) {
						panels.push({ width: wmWidth, color });
					}
					colorIndex++;
				} else {
					// Non-wall-mounted product: just increment color index if it has valid size
					const sizeData = product.data.sizes?.items?.[product.size];
					if (sizeData && sizeData.id) {
						colorIndex++;
					}
				}
			}

			ctx.lineWidth = 3; // Thicker lines for wall-mounted panels

			const roomWidthMm = parseInt(
				store.user.room.unit === "imperial"
					? Utils.convertFeetToMm(roomWidth)
					: roomWidth * 1000,
			);
			const roomLengthMm = parseInt(
				store.user.room.unit === "imperial"
					? Utils.convertFeetToMm(roomLength)
					: roomLength * 1000,
			);
			const scaleFactor = drawableWidth.value / roomWidthMm;
			const strokeOffset = 6;

			// Wall segment boundaries (in mm from start of perimeter)
			const wallSegments = [
				{ length: roomWidthMm, direction: "right" }, // TOP
				{ length: roomLengthMm, direction: "down" }, // RIGHT
				{ length: roomWidthMm, direction: "left" }, // BOTTOM
				{ length: roomLengthMm, direction: "up" }, // LEFT
			];

			const totalPerimeterMm = wallSegments.reduce(
				(sum, seg) => sum + seg.length,
				0,
			);

			// Helper to find the end of the wall segment containing posMm
			const getWallEndMm = (posMm) => {
				let offset = 0;
				for (const seg of wallSegments) {
					offset += seg.length;
					if (posMm < offset) return offset;
				}
				return totalPerimeterMm;
			};

			const panelPositions = [];

			if (DISTRIBUTE_WALL_PANELS) {
				// DISTRIBUTED MODE: Panels evenly spaced around the perimeter
				// with equal gaps between them (and at the start/end).
				// If a panel would span a wall corner, it's pushed to the next wall.
				const totalPanelWidthMm = panels.reduce((sum, p) => sum + p.width, 0);
				const totalGapMm = Math.max(0, totalPerimeterMm - totalPanelWidthMm);
				const gapMm = panels.length > 0 ? totalGapMm / (panels.length + 1) : 0;

				let perimeterPosMm = gapMm;
				panels.forEach((panel) => {
					let wallEnd = getWallEndMm(perimeterPosMm);
					while (
						panel.width >= wallEnd - perimeterPosMm &&
						perimeterPosMm < totalPerimeterMm
					) {
						perimeterPosMm = wallEnd + WALL_MOUNTED_GAP_MM;
						wallEnd = getWallEndMm(perimeterPosMm);
					}

					if (perimeterPosMm + panel.width <= totalPerimeterMm) {
						panelPositions.push({
							startMm: perimeterPosMm,
							endMm: perimeterPosMm + panel.width,
							color: panel.color,
						});
					}
					perimeterPosMm += panel.width + gapMm;
				});
			} else {
				// SEQUENTIAL MODE: Panels placed side-by-side starting from the
				// top wall with a small gap between them. If a panel would span
				// a wall corner, it's pushed to the next wall.
				let perimeterPosMm = WALL_MOUNTED_GAP_MM;
				panels.forEach((panel) => {
					let wallEnd = getWallEndMm(perimeterPosMm);
					while (
						panel.width >= wallEnd - perimeterPosMm &&
						perimeterPosMm < totalPerimeterMm
					) {
						perimeterPosMm = wallEnd + WALL_MOUNTED_GAP_MM;
						wallEnd = getWallEndMm(perimeterPosMm);
					}

					if (perimeterPosMm + panel.width <= totalPerimeterMm) {
						panelPositions.push({
							startMm: perimeterPosMm,
							endMm: perimeterPosMm + panel.width,
							color: panel.color,
						});
					}
					perimeterPosMm += panel.width + WALL_MOUNTED_GAP_MM;
				});
			}

			// Draw each panel at its computed position
			panelPositions.forEach((pos) => {
				const panelStartMm = pos.startMm;
				const panelEndMm = pos.endMm;

				// Set the stroke color for this panel
				ctx.strokeStyle = pos.color;

				// Draw this panel (may span multiple wall segments)
				let drawStartMm = panelStartMm;
				let drawEndMm = panelEndMm;
				let wallStartMm = 0;

				for (let segIdx = 0; segIdx < wallSegments.length; segIdx++) {
					const seg = wallSegments[segIdx];
					const wallEndMm = wallStartMm + seg.length;

					// Check if panel overlaps this wall segment
					if (drawEndMm > wallStartMm && drawStartMm < wallEndMm) {
						// Calculate the portion of the panel on this wall
						const segStartMm = Math.max(drawStartMm, wallStartMm);
						const segEndMm = Math.min(drawEndMm, wallEndMm);
						const segStartPx = (segStartMm - wallStartMm) * scaleFactor;
						const segEndPx = (segEndMm - wallStartMm) * scaleFactor;

						// Convert to canvas coordinates based on wall direction
						let x1, y1, x2, y2;
						if (segIdx === 0) {
							// TOP wall (left to right)
							x1 = padding.value + strokeOffset + segStartPx;
							y1 = padding.value + strokeOffset;
							x2 = padding.value + strokeOffset + segEndPx;
							y2 = y1;
						} else if (segIdx === 1) {
							// RIGHT wall (top to bottom)
							x1 = padding.value + drawableWidth.value - strokeOffset;
							y1 = padding.value + strokeOffset + segStartPx;
							x2 = x1;
							y2 = padding.value + strokeOffset + segEndPx;
						} else if (segIdx === 2) {
							// BOTTOM wall (right to left)
							x1 =
								padding.value + drawableWidth.value - strokeOffset - segStartPx;
							y1 = padding.value + drawableHeight.value - strokeOffset;
							x2 =
								padding.value + drawableWidth.value - strokeOffset - segEndPx;
							y2 = y1;
						} else {
							// LEFT wall (bottom to top)
							x1 = padding.value + strokeOffset;
							y1 =
								padding.value +
								drawableHeight.value -
								strokeOffset -
								segStartPx;
							x2 = x1;
							y2 =
								padding.value + drawableHeight.value - strokeOffset - segEndPx;
						}

						ctx.beginPath();
						ctx.moveTo(x1, y1);
						ctx.lineTo(x2, y2);
						ctx.stroke();
					}

					wallStartMm = wallEndMm;
				}
			});
		} // End of else block (wall-mounted products visible)
	}

	// Draw grid dots and additional elements
	setTimeout(() => {
		ctx.fillStyle = "#000000";
		for (let i = 1; i < roomWidth; i++) {
			for (let j = 1; j < roomLength; j++) {
				const x = padding.value + i * gridStep.value;
				const y = padding.value + j * gridStep.value;
				ctx.beginPath();
				ctx.arc(x, y, dotRadius.value, 0, 2 * Math.PI);
				ctx.fill();
			}
		}
		// Only draw canvas legend during export (HTML legend handles interactive mode)
		if (isExporting.value) {
			const legendStartY = padding.value + drawableHeight.value + padding.value;
			drawLegend(ctx, legendStartY, width);
		}

		setTimeout(() => {
			drawFurnitureImage(ctx, width, height);
			setTimeout(() => {
				// Draw all products except wall-mounted products (drawn as wall segments above)
				drawProductImages(ctx, width, height);
			}, 150);
		}, 50);
	}, 50);
};

/*
------------------------------------------------------------------------
Insert/draw the furniture image and size to scale relative to the room
and product sizes.
------------------------------------------------------------------------
*/
const drawFurnitureImage = (ctx, width, height, scale = 1) => {
	// The available furnitre images.
	const furnitureOptions = {
		zero: { w: 820, h: 500, url: `furniture-0.svg` },
		one: { w: 1300, h: 1300, url: `furniture-1.svg` },
		two: { w: 1746, h: 1300, url: `furniture-2.svg` },
		three: { w: 2193, h: 1300, url: `furniture-3.svg` },
		four: { w: 2639, h: 1300, url: `furniture-4.svg` },
		five: { w: 3085, h: 1300, url: `furniture-5.svg` },
		six: { w: 3531, h: 1300, url: `furniture-6.svg` },
		seven: { w: 3977, h: 1300, url: `furniture-7.svg` },
		eight: { w: 4423, h: 1300, url: `furniture-8.svg` },
		nine: { w: 4870, h: 1300, url: `furniture-9.svg` },
		ten: { w: 5316, h: 1300, url: `furniture-10.svg` },
	};

	// Determine the longest side value
	const longestSideValue = Math.max(
		store.user.room.width,
		store.user.room.length,
	);
	const shortestSideValue = Math.min(
		store.user.room.width,
		store.user.room.length,
	);

	// Determine which furnitrue image to use relative to the room dimensions.
	if (store.user.room.unit === "imperial") {
		if (shortestSideValue < 10) {
			furniture.value.selected = "zero";
		} else {
			if (longestSideValue < 10) {
				furniture.value.selected = "one";
			} else {
				if (longestSideValue >= 10) {
					furniture.value.selected = "one";
				}
				if (longestSideValue >= 18) {
					furniture.value.selected = "two";
				}
				if (longestSideValue >= 20) {
					furniture.value.selected = "three";
				}
				if (longestSideValue >= 25) {
					furniture.value.selected = "four";
				}
				if (longestSideValue >= 30) {
					furniture.value.selected = "five";
				}
				if (longestSideValue >= 35) {
					furniture.value.selected = "six";
				}
				if (longestSideValue >= 40) {
					furniture.value.selected = "seven";
				}
				if (longestSideValue >= 45) {
					furniture.value.selected = "eight";
				}
				if (longestSideValue >= 50) {
					furniture.value.selected = "nine";
				}
				if (longestSideValue >= 55) {
					furniture.value.selected = "ten";
				}
			}
		}
	} else {
		if (shortestSideValue < 3) {
			furniture.value.selected = "zero";
		} else {
			if (longestSideValue < 3) {
				furniture.value.selected = "zero";
			} else {
				if (longestSideValue >= 3) {
					furniture.value.selected = "one";
				}
				if (longestSideValue >= 7) {
					furniture.value.selected = "two";
				}
				if (longestSideValue >= 9) {
					furniture.value.selected = "three";
				}
				if (longestSideValue >= 11) {
					furniture.value.selected = "four";
				}
				if (longestSideValue >= 12) {
					furniture.value.selected = "five";
				}
				if (longestSideValue >= 13) {
					furniture.value.selected = "six";
				}
				if (longestSideValue >= 15) {
					furniture.value.selected = "seven";
				}
				if (longestSideValue >= 16) {
					furniture.value.selected = "eight";
				}
				if (longestSideValue >= 18) {
					furniture.value.selected = "nine";
				}
				if (longestSideValue >= 20) {
					furniture.value.selected = "ten";
				}
			}
		}
	}

	const selectedFurniture = furnitureOptions[furniture.value.selected];
	const selectedFurnitureWidthMM = selectedFurniture?.w || 0;
	const selectedFurnitureHeightMM = selectedFurniture?.h || 9;

	if (selectedFurniture?.url) {
		// Placeholder for furniture embedding logic
		const svgUrl = Utils.getFurnitureImageUrl(selectedFurniture.url);
		const img = new Image();

		img.onload = () => {
			// Calculate the relative width and height of the furniture based on the unit
			// and scale while keeping aspect ratio.
			let furnitureWidth, furnitureHeight;

			if (store.user.room.unit === "metric") {
				furnitureWidth =
					(selectedFurnitureWidthMM / 1000) * gridStep.value * 1.8;
				furnitureHeight =
					(selectedFurnitureHeightMM / 1000) * gridStep.value * 1.8;
			} else {
				furnitureWidth =
					selectedFurnitureWidthMM *
					MM_TO_FEET_CONVERSION *
					gridStep.value *
					1.9;
				furnitureHeight =
					selectedFurnitureHeightMM *
					MM_TO_FEET_CONVERSION *
					gridStep.value *
					1.9;
			}

			// Maintain the aspect ratio
			const originalAspectRatio = img.width / img.height;
			if (furnitureWidth / furnitureHeight > originalAspectRatio) {
				// If calculated width is larger, adjust it based on height
				furnitureWidth = furnitureHeight * originalAspectRatio;
			} else {
				// If calculated height is larger, adjust it based on width
				furnitureHeight = furnitureWidth / originalAspectRatio;
			}

			// Once the image loads, draw it onto the canvas at the center of the ROOM area
			// (not the full canvas which includes the legend)
			const imageWidth = furnitureWidth * scale;
			const imageHeight = furnitureHeight * scale;
			const roomCenterX = padding.value + drawableWidth.value / 2;
			const roomCenterY = padding.value + drawableHeight.value / 2;
			const centerX = roomCenterX - imageWidth / 2;
			const centerY = roomCenterY - imageHeight / 2;

			// Draw image
			ctx.drawImage(img, centerX, centerY, imageWidth, imageHeight);
		};

		img.onerror = (error) => {
			console.error("Failed to load SVG image:", error);
		};

		// Allow cross-origin requests for the image URL.
		img.crossOrigin = "anonymous";
		img.src = svgUrl;
	}
};

/*
------------------------------------------------------------------------
Calculates the positions of the products.

MERGED GROUPING LOGIC:
Products with the same product code AND size are merged into a single
positioning group. This prevents overlapping when the same product is
selected multiple times with different options (e.g., lit vs non-lit).

Each unit tracks its `originalProductId` to preserve:
- Individual color assignment (matches legend)
- Per-selection visibility toggle support

VISIBILITY BEHAVIOR:
- Hidden products are excluded from positioning calculation (not just drawing)
- This causes remaining visible products to recentre automatically
- During export (isExporting=true), all products are included regardless of visibility

Each merged group is centered independently at the room center.
------------------------------------------------------------------------
*/
const updateProductPositions = () => {
	const roomWidth = drawableWidth.value;
	const roomLength = drawableHeight.value;

	// Guard: Don't process if dimensions are invalid
	if (roomWidth <= 0 || roomLength <= 0) {
		return;
	}

	/*
	------------------------------------------------------------------------
	STEP 1: Build merged groups by productCode_size key

	Products with same code AND size are merged for positioning purposes.
	This prevents overlap when same product is added multiple times with
	different options (e.g., Georgina Medium lit + Georgina Medium non-lit).

	Each unit stores its originalProductId for color/visibility lookup later.
	------------------------------------------------------------------------
	*/
	const mergedGroups = new Map(); // key: "GR_Medium", value: { units: [], ... }

	store.user.products.forEach((product) => {
		// Skip wall-mounted products - they are rendered as wall segments
		if (WALL_MOUNTED_PRODUCT_IDS.includes(product.data?.id)) {
			return;
		}

		if (!product.data || !product.size) return;

		// Skip hidden products during positioning (unless exporting)
		// This causes remaining visible products to recentre automatically
		if (!isExporting.value && !isProductVisible(product.id)) {
			return;
		}

		// Merge key: productCode_shape_size (e.g., "GR_Medium" or "TE_L_2")
		// Products with SAME code, shape, AND size are merged
		const mergeKey = product.shape
			? `${product.product}_${product.shape}_${product.size}`
			: `${product.product}_${product.size}`;

		// Get or create the merged group for this combination
		if (!mergedGroups.has(mergeKey)) {
			// First product with this code_shape_size - calculate dimensions once
			const dims = convertProductDimensionsForProduct(product);
			const actualLongerDim = Math.max(dims.productWidth, dims.productLength);
			const actualShorterDim = Math.min(dims.productWidth, dims.productLength);
			const isElongated = actualLongerDim > actualShorterDim * 2;
			const dimensionsSwapped = dims.productWidth > dims.productLength;

			mergedGroups.set(mergeKey, {
				units: [],
				productCode: product.product,
				shape: product.shape || "",
				size: product.size,
				product: product,
				dims: dims,
				layoutWidth: actualShorterDim,
				layoutLength: actualLongerDim,
				dimensionsSwapped: dimensionsSwapped,
				isElongated: isElongated,
			});
		}

		const group = mergedGroups.get(mergeKey);
		const quantity =
			product.manualUnits ?? product.calculation?.calculatedUnits ?? 1;

		// Add units for this product to the merged group
		// Each unit tracks its originalProductId for color assignment during drawing
		for (let i = 0; i < quantity; i++) {
			group.units.push({
				...group.dims,
				layoutWidth: group.layoutWidth,
				layoutLength: group.layoutLength,
				dimensionsSwapped: group.dimensionsSwapped,
				isElongated: group.isElongated,
				originalProductId: product.id, // Track for color + visibility lookup
			});
		}
	});

	// Convert Map to array for processing
	const productGroups = Array.from(mergedGroups.values()).filter(
		(group) => group.units.length > 0,
	);

	if (productGroups.length === 0) {
		productPositions.value = [];
		return;
	}

	// Reset warning flag
	productTooBigForRoom.value = false;

	// Calculate room center (absolute center)
	const roomCenterX = padding.value + roomWidth / 2;
	const roomCenterY = padding.value + roomLength / 2;

	// Store positions with product group info
	const newProductPositions = [];

	// Process each product group independently - each group centers itself at the absolute room center
	productGroups.forEach((group) => {
		const units = group.units;
		const productWidth = units[0].productWidth;
		const productLength = units[0].productLength;
		const isElongated = group.isElongated;

		// Calculate product-specific spacing for this group
		const groupSpacing = CanvasUtils.calculateProductSpacing(
			group.productCode,
			units.length,
			roomWidth - 60,
			productWidth,
			gridStep.value,
		);

		// Determine room orientation (width >= length means wider or square)
		const roomIsWiderThanLong = roomWidth >= roomLength;

		// Determine product orientation for elongated products
		// Goal: product's longest edge should run parallel to room's longest edge
		// Must account for SVG's natural orientation:
		// - If productWidth > productLength: SVG is naturally horizontal (e.g., Blade)
		// - If productLength > productWidth: SVG is naturally vertical (e.g., Kurtain Line)
		const svgIsNaturallyHorizontal = units[0].dimensionsSwapped;

		let rotateProduct = false;
		if (isElongated) {
			if (roomIsWiderThanLong) {
				// Wide room: want products horizontal
				// Rotate only if SVG is naturally vertical
				rotateProduct = !svgIsNaturallyHorizontal;
			} else {
				// Tall room: want products vertical
				// Rotate only if SVG is naturally horizontal
				rotateProduct = svgIsNaturallyHorizontal;
			}

			// Check if the arrangement will actually fit using actual layout dimensions
			const layoutLongDim = units[0].layoutLength;
			const layoutShortDim = units[0].layoutWidth;

			if (!rotateProduct && layoutLongDim > roomWidth) {
				// Product too long for horizontal, try vertical
				rotateProduct = true;
			} else if (rotateProduct && layoutLongDim > roomLength) {
				// Product too long for vertical, try horizontal
				rotateProduct = false;
			}

			// Final check: if product won't fit in ANY orientation, still render but flag it
			const productTooBig =
				(layoutLongDim > roomWidth && layoutLongDim > roomLength) ||
				(layoutShortDim > roomWidth && layoutShortDim > roomLength);

			if (productTooBig) {
				productTooBigForRoom.value = true;
			}
		}

		// Calculate grid layout for this group
		// Each group centers itself at the absolute room center, ignoring other groups
		let groupPositions = [];

		if (isElongated && rotateProduct) {
			// Elongated product, ROTATED 90 degrees
			// When rotated: screen width = productLength, screen height = productWidth
			// Products are arranged in columns (stacking vertically by productWidth)
			const screenWidth = productLength;
			const screenHeight = productWidth;

			// Stack vertically along roomLength, check threshold against screenHeight
			const gridConfig = CanvasUtils.calculateOptimalGrid(
				group.productCode,
				units.length,
				roomLength,
				screenHeight,
				groupSpacing,
			);
			const itemsPerCol = gridConfig.itemsPerRow;
			const numCols = gridConfig.numRows;

			const totalGridWidth =
				numCols * screenWidth + (numCols - 1) * groupSpacing;

			for (let i = 0; i < units.length; i++) {
				const col = Math.floor(i / itemsPerCol);
				const row = i % itemsPerCol;
				const productsInThisCol = Math.min(
					itemsPerCol,
					units.length - col * itemsPerCol,
				);
				const colHeight =
					productsInThisCol * screenHeight +
					(productsInThisCol - 1) * groupSpacing;

				// Center this group's grid at the absolute room center
				const startX = roomCenterX - totalGridWidth / 2;
				const startY = roomCenterY - colHeight / 2;

				const x = startX + col * (screenWidth + groupSpacing);
				const y = startY + row * (screenHeight + groupSpacing);

				// Store position with originalProductId for color lookup during drawing
				groupPositions.push({
					x,
					y,
					rotated: true,
					originalProductId: units[i].originalProductId,
					productCode: group.productCode,
					shape: group.shape,
					size: group.size,
				});
			}
		} else if (isElongated && !rotateProduct) {
			// Elongated product, NOT rotated
			// When not rotated: screen width = productWidth, screen height = productLength
			// Products are arranged in rows (stacking vertically by productLength)
			const screenWidth = productWidth;
			const screenHeight = productLength;

			// Stack vertically along roomLength, check threshold against screenHeight
			const gridConfig = CanvasUtils.calculateOptimalGrid(
				group.productCode,
				units.length,
				roomLength,
				screenHeight,
				groupSpacing,
			);
			const itemsPerCol = gridConfig.itemsPerRow;
			const numCols = gridConfig.numRows;

			const totalGridWidth =
				numCols * screenWidth + (numCols - 1) * groupSpacing;

			for (let i = 0; i < units.length; i++) {
				const col = Math.floor(i / itemsPerCol);
				const row = i % itemsPerCol;
				const productsInThisCol = Math.min(
					itemsPerCol,
					units.length - col * itemsPerCol,
				);
				const colHeight =
					productsInThisCol * screenHeight +
					(productsInThisCol - 1) * groupSpacing;

				// Center this group's grid at the absolute room center
				const startX = roomCenterX - totalGridWidth / 2;
				const startY = roomCenterY - colHeight / 2;

				const x = startX + col * (screenWidth + groupSpacing);
				const y = startY + row * (screenHeight + groupSpacing);

				// Store position with originalProductId for color lookup during drawing
				groupPositions.push({
					x,
					y,
					rotated: false,
					originalProductId: units[i].originalProductId,
					productCode: group.productCode,
					shape: group.shape,
					size: group.size,
				});
			}
		} else {
			// Normal aspect ratio - standard grid
			// Stack horizontally along roomWidth, use calculateOptimalGrid for threshold
			const gridConfig = CanvasUtils.calculateOptimalGrid(
				group.productCode,
				units.length,
				roomWidth,
				productWidth,
				groupSpacing,
			);
			const itemsPerRow = gridConfig.itemsPerRow;
			const numRows = gridConfig.numRows;

			const gridHeight = numRows * productLength + (numRows - 1) * groupSpacing;

			for (let i = 0; i < units.length; i++) {
				const row = Math.floor(i / itemsPerRow);
				const col = i % itemsPerRow;
				const productsInThisRow = Math.min(
					itemsPerRow,
					units.length - row * itemsPerRow,
				);
				const gridWidth =
					productsInThisRow * productWidth +
					(productsInThisRow - 1) * groupSpacing;

				// Center this group's grid at the absolute room center
				const baseStartX = roomCenterX - gridWidth / 2;
				const baseStartY = roomCenterY - gridHeight / 2;

				const x = baseStartX + col * (productWidth + groupSpacing);
				const y = baseStartY + row * (productLength + groupSpacing);

				// Store position with originalProductId for color lookup during drawing
				groupPositions.push({
					x,
					y,
					rotated: false,
					originalProductId: units[i].originalProductId,
					productCode: group.productCode,
					shape: group.shape,
					size: group.size,
				});
			}
		}

		// Add all positions for this group
		newProductPositions.push(...groupPositions);
	});

	productPositions.value = newProductPositions;
};

/*
------------------------------------------------------------------------
Prefetch SVG viewBox dimensions for shape products.

Shape products (e.g., Tessell L, T, X, Y) share a single set of catalog
dimensions per size, but each shape has a different physical footprint.
The SVGs are authored at 1:1mm scale, so the viewBox width/height give
the true footprint. This function fetches those SVGs, parses the viewBox,
and caches { width, height, svgText } so that positioning and drawing
can use the correct dimensions without double-fetching.
------------------------------------------------------------------------
*/
const prefetchShapeSvgDimensions = async () => {
	const seen = new Set();

	for (const product of store.user.products) {
		if (!product.data?.hasShapes || !product.shape || !product.size) {
			continue;
		}

		const cacheKey = `${product.product}_${product.shape}_${product.size}`;
		if (seen.has(cacheKey) || svgDimensionCache.has(cacheKey)) {
			continue;
		}
		seen.add(cacheKey);

		const svgFilename = `LX-${product.product}-${product.shape}-${product.size}.svg`;
		const svgUrl = Utils.getProductImageUrl(
			`${product.product}/${svgFilename}`,
		);

		try {
			const response = await fetch(svgUrl);
			if (!response.ok) {
				$log.warn(
					`[RoomCanvas:prefetchShapeSvgDimensions] SVG not found (${response.status}): ${svgFilename}`,
				);
				continue;
			}

			const svgText = await response.text();
			const parser = new DOMParser();
			const svgDoc = parser.parseFromString(svgText, "image/svg+xml");
			const svgEl = svgDoc.documentElement;
			const viewBox = svgEl.getAttribute("viewBox");

			if (viewBox) {
				const parts = viewBox.split(/[\s,]+/);
				if (parts.length === 4) {
					const width = parseFloat(parts[2]);
					const height = parseFloat(parts[3]);
					if (width > 0 && height > 0) {
						svgDimensionCache.set(cacheKey, { width, height, svgText });
						$log.log(
							`[RoomCanvas:prefetchShapeSvgDimensions] Cached ${cacheKey}: ${width}x${height}mm`,
						);
					}
				}
			}
		} catch (error) {
			$log.warn(
				`[RoomCanvas:prefetchShapeSvgDimensions] Error fetching ${svgFilename}:`,
				error,
			);
		}
	}
};

/*
------------------------------------------------------------------------
Draw products on the canvas at the calculated positions

COLOR ASSIGNMENT:
Colors are assigned based on the order products appear in store.user.products.
Each product selection gets a unique color that matches its legend entry.

Since positions may be from merged groups (same productCode_size), we use
originalProductId to look up the correct color for each individual unit.

GROUPING FOR SVG FETCH:
Positions are grouped by productCode_size for efficient SVG fetching
(same SVG can be reused for all units of same product type/size).
------------------------------------------------------------------------
*/
const drawProductImages = async (ctx) => {
	// Prefetch SVG viewBox dimensions for shape products (e.g., Tessell L/T/X/Y)
	// so that convertProductDimensionsForProduct() can use correct footprints
	await prefetchShapeSvgDimensions();

	// Get the product positions (includes merged group logic)
	updateProductPositions();

	/*
	------------------------------------------------------------------------
	STEP 1: Build color map (productId → color)

	Colors are assigned in order of store.user.products to match the legend.
	Wall-mounted products are included in color indexing but not drawn here.
	------------------------------------------------------------------------
	*/
	const productColorMap = new Map(); // productId → color
	let colorIndex = 0;

	for (const product of store.user.products) {
		if (!product.data || !product.size) continue;

		// Assign color to this product (matches legend assignment)
		productColorMap.set(
			product.id,
			PRODUCT_COLORS[colorIndex % PRODUCT_COLORS.length],
		);
		colorIndex++;
	}

	$log.log(
		"[RoomCanvas:drawProductImages] productColorMap size:",
		productColorMap.size,
	);
	$log.log(
		"[RoomCanvas:drawProductImages] positions count:",
		productPositions.value.length,
	);

	/*
	------------------------------------------------------------------------
	STEP 2: Group positions by productCode_shape_size for efficient SVG fetching

	Since merged groups may contain units from different product selections
	(same code/shape/size but different options), we group to reuse the same
	SVG. The color for each unit is looked up via originalProductId.
	------------------------------------------------------------------------
	*/
	const positionsByCodeSize = new Map();

	productPositions.value.forEach((pos) => {
		const groupKey = pos.shape
			? `${pos.productCode}_${pos.shape}_${pos.size}`
			: `${pos.productCode}_${pos.size}`;

		if (!positionsByCodeSize.has(groupKey)) {
			// Find the first visible product with this code_shape_size for SVG lookup
			const matchingProduct = store.user.products.find(
				(p) =>
					p.product === pos.productCode &&
					p.size === pos.size &&
					(pos.shape ? p.shape === pos.shape : true) &&
					p.data &&
					p.data.sizes?.items?.[p.size],
			);

			positionsByCodeSize.set(groupKey, {
				positions: [],
				product: matchingProduct,
				productCode: pos.productCode,
				shape: pos.shape || "",
				size: pos.size,
			});
		}

		positionsByCodeSize.get(groupKey).positions.push(pos);
	});

	/*
	------------------------------------------------------------------------
	STEP 3: Draw each productCode_size group

	For each group, fetch the SVG once, then draw each unit with its
	individual color based on originalProductId.
	------------------------------------------------------------------------
	*/
	for (const [groupKey, group] of positionsByCodeSize) {
		const { positions, product, productCode } = group;

		if (!product || positions.length === 0) {
			$log.warn(
				`[RoomCanvas:drawProductImages] No valid product found for group: ${groupKey}`,
			);
			continue;
		}

		// Skip wall-mounted products (they're drawn as wall segments in drawCanvas)
		if (WALL_MOUNTED_PRODUCT_IDS.includes(product.data?.id)) {
			continue;
		}

		const sizeData = product.data.sizes.items[product.size];
		if (!sizeData || !sizeData.id) {
			$log.warn(
				"[RoomCanvas:drawProductImages] Missing size data for product:",
				productCode,
			);
			continue;
		}

		// Build SVG filename — shape-aware for products with shape variants
		const shape = group.shape;
		const hasShapes = product.data?.hasShapes === true;
		let svgFilename;
		if (hasShapes && shape) {
			svgFilename = `LX-${productCode}-${shape}-${product.size}.svg`;
		} else {
			svgFilename = `${sizeData.id}.svg`;
		}

		// Fetch the product SVG (reused for all units in this group)
		// Use cached SVG text for shape products to avoid re-fetching
		try {
			const cacheKey =
				hasShapes && shape ? `${productCode}_${shape}_${product.size}` : null;
			const cached = cacheKey ? svgDimensionCache.get(cacheKey) : null;
			let productImageData;

			if (cached?.svgText) {
				productImageData = cached.svgText;
			} else {
				const svgUrl = Utils.getProductImageUrl(
					`${productCode}/${svgFilename}`,
				);
				const productImageResponse = await fetch(svgUrl);
				if (!productImageResponse.ok) {
					$log.warn(
						`[RoomCanvas:drawProductImages] SVG not found (${productImageResponse.status}): ${svgFilename}`,
					);
					continue;
				}
				productImageData = await productImageResponse.text();
			}

			// Calculate dimensions for this product
			const dims = convertProductDimensionsForProduct(product);

			// Draw each unit with its individual color based on originalProductId
			positions.forEach((position) => {
				// Look up color for this specific unit's original product
				const strokeColor = productColorMap.get(position.originalProductId);

				if (!strokeColor) {
					$log.warn(
						`[RoomCanvas:drawProductImages] No color found for originalProductId: ${position.originalProductId}`,
					);
					return;
				}
				try {
					// Parse and set properties of the SVG
					const parser = new DOMParser();
					const svgDoc = parser.parseFromString(
						productImageData,
						"image/svg+xml",
					);
					const svgElement = svgDoc.documentElement;

					// Set stroke on root SVG element
					svgElement.setAttribute("fill", "none");
					svgElement.setAttribute("stroke", strokeColor);
					svgElement.setAttribute("stroke-linecap", "round");
					svgElement.setAttribute("stroke-linejoin", "round");
					svgElement.setAttribute("stroke-width", PRODUCT_STROKE_WIDTH);

					// Also set stroke on all child elements that can have strokes
					// This ensures the color is applied even if child elements have their own stroke attributes
					const strokeElements = svgElement.querySelectorAll(
						"path, line, circle, ellipse, rect, polygon, polyline",
					);
					strokeElements.forEach((el) => {
						el.setAttribute("stroke", strokeColor);
						el.setAttribute("stroke-width", PRODUCT_STROKE_WIDTH);
						// Remove any fill to ensure only stroke shows
						if (el.getAttribute("fill") !== "none") {
							el.setAttribute("fill", "none");
						}
						// Remove inline style that might override stroke color
						const style = el.getAttribute("style");
						if (style) {
							// Remove stroke-related styles from inline style attribute
							const newStyle = style
								.replace(/stroke\s*:[^;]+;?/gi, "")
								.replace(/stroke-width\s*:[^;]+;?/gi, "")
								.trim();
							if (newStyle) {
								el.setAttribute("style", newStyle);
							} else {
								el.removeAttribute("style");
							}
						}
					});

					// Also check for style tags and remove stroke rules
					const styleTags = svgElement.querySelectorAll("style");
					styleTags.forEach((styleTag) => {
						styleTag.textContent = styleTag.textContent
							.replace(/stroke\s*:[^;]+;?/gi, `stroke:${strokeColor};`)
							.replace(
								/stroke-width\s*:[^;]+;?/gi,
								`stroke-width:${PRODUCT_STROKE_WIDTH};`,
							);
					});

					const serializer = new XMLSerializer();
					const modifiedSvgString = serializer.serializeToString(svgElement);

					// Load SVG into an image
					const img = new Image();
					img.onload = () => {
						const { x, y, rotated } = position;

						if (rotated) {
							// Save context state
							ctx.save();

							// Move to the position and rotate 90 degrees clockwise
							ctx.translate(x + dims.productLength, y);
							ctx.rotate(Math.PI / 2);

							// Draw the image (dimensions are swapped because of rotation)
							ctx.drawImage(img, 0, 0, dims.productWidth, dims.productLength);

							// Restore context state
							ctx.restore();
						} else {
							// Draw normally without rotation
							ctx.drawImage(img, x, y, dims.productWidth, dims.productLength);
						}
					};
					img.onerror = (error) => {
						console.error("Failed to load SVG image:", error);
					};
					img.crossOrigin = "anonymous";
					img.src = "data:image/svg+xml;base64," + btoa(modifiedSvgString);
				} catch (error) {
					console.error("Error drawing product image:", error);
				}
			});
		} catch (error) {
			console.error("Error fetching product image:", error);
		}
	}
};

/*
---------------------------------------------------------------------------
Convert mm to feet or meters based on room units (for a specific product)
---------------------------------------------------------------------------
*/
const convertProductDimensionsForProduct = (product) => {
	let productWidth = 0;
	let productLength = 0;

	if (!product || !product.data || !product.size) {
		return { productWidth: 0, productLength: 0 };
	}

	const selectedSize = product.data.sizes.items[product.size];
	if (!selectedSize) {
		return { productWidth: 0, productLength: 0 };
	}

	// Use catalog dimensions by default
	let widthMm = parseFloat(selectedSize.w);
	let depthMm = parseFloat(selectedSize.d);

	// For shape products, derive correct footprint from SVG viewBox.
	// Shape SVGs come in two coordinate conventions:
	//   1. mm-scale (ratio ≤ 1.2): viewBox ≈ real mm (e.g., Y shape)
	//   2. Scaled system (ratio > 1.2): viewBox units ≠ mm (e.g., L/T/X)
	// For group 2, the viewBox may span the full catalog diameter D (T, X
	// where ratio > 2.0) or half-diameter D/2 (L where ratio 1.2–2.0).
	// Scale uniformly so that the target dimension (D or D/2) maps to the
	// viewBox's longest side, preserving aspect ratio without distortion.
	if (product.data?.hasShapes && product.shape) {
		const cacheKey = `${product.product}_${product.shape}_${product.size}`;
		const cached = svgDimensionCache.get(cacheKey);
		if (cached) {
			const catalogMax = Math.max(widthMm, depthMm);
			const viewBoxMax = Math.max(cached.width, cached.height);
			const ratio = viewBoxMax / catalogMax;

			if (ratio <= 1.2) {
				// SVG is in mm — use viewBox directly
				widthMm = cached.width;
				depthMm = cached.height;
			} else {
				// Full-diameter shapes (ratio > 2.0): target = D
				// Half-diameter shapes (ratio 1.2–2.0): target = D/2
				const target = ratio > 2.0 ? catalogMax : catalogMax / 2;
				const scale = target / viewBoxMax;
				widthMm = cached.width * scale;
				depthMm = cached.height * scale;
			}
		}
	}

	if (store.user.room.unit === "metric") {
		productWidth = (widthMm / 1000) * gridStep.value;
		productLength = (depthMm / 1000) * gridStep.value;
	} else {
		productWidth = Utils.convertMmToFeet(widthMm) * gridStep.value;
		productLength = Utils.convertMmToFeet(depthMm) * gridStep.value;
	}

	return { productWidth, productLength };
};

/*
------------------------------------------------------------------------
Export Canvas and send the image to the server

During export:
- Sets isExporting flag to true
- Redraws canvas with ALL products visible and legend drawn on canvas
- Exports the canvas as PNG
- Resets isExporting flag and optionally redraws to restore normal state
------------------------------------------------------------------------
*/
const exportCanvasAsImage = async () => {
	$log.log(`[RoomCanvas:exportCanvasAsImage] Starting canvas export`);

	// Check if canvas ref exists
	if (!canvas.value) {
		$log.warn(
			"[RoomCanvas:exportCanvasAsImage] Canvas ref is null, cannot export image",
		);
		store.user.roomMockupImageBase64 = "";
		return;
	}

	// Set export mode - this makes the canvas draw all products and the legend
	isExporting.value = true;

	// Redraw the canvas with export settings (all products visible, legend on canvas)
	redrawCanvasOnly();

	// Wait for the canvas drawing to complete
	// Drawing uses nested setTimeouts (50ms + 50ms + 150ms = 250ms), so we wait 500ms to be safe
	await new Promise((resolve) => setTimeout(resolve, 500));

	// Check if canvas has dimensions (is rendered)
	const canvasWidth = canvas.value.width;
	const canvasHeight = canvas.value.height;
	$log.log(
		`[RoomCanvas:exportCanvasAsImage] Canvas dimensions: ${canvasWidth}x${canvasHeight}`,
	);

	if (canvasWidth === 0 || canvasHeight === 0) {
		$log.warn(
			"[RoomCanvas:exportCanvasAsImage] Canvas has zero dimensions, cannot export image",
		);
		store.user.roomMockupImageBase64 = "";
		isExporting.value = false;
		return;
	}

	// Export the canvas as PNG
	const image = canvas.value.toDataURL("image/png");

	// Validate the exported image
	if (!image || image === "data:," || image.length < 100) {
		$log.warn(
			`[RoomCanvas:exportCanvasAsImage] Canvas export returned empty or invalid data: ${image.substring(0, 50)}`,
		);
	} else {
		$log.log(
			`[RoomCanvas:exportCanvasAsImage] Canvas exported successfully, data length: ${image.length}`,
		);
	}

	// Store base64 image data for DOCX generation (no Google Drive round trip needed)
	store.user.roomMockupImageBase64 = image;

	// Payload for the save-img endpoint (still needed for Google Docs API backward compatibility)
	// $log.log(`store.user.user_id : ${JSON.stringify(store.user.user_id, null, 4)}`);
	const data = {
		image: image,
		filename:
			store.user.user_id +
			"_" +
			store.user.firstname +
			"_" +
			store.user.lastname +
			"_room_mockup.png",
	};
	$log.log(
		`[RoomCanvas:exportCanvasAsImage] data: ${JSON.stringify(data, null, 4)}`,
	);
	try {
		const response = await fetch("/wp-json/lat/v1/save-svg", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(data),
		});
		const result = await response.json();
		// $log.log(`result : ${JSON.stringify(result, null, 4)}`);
		if (response.ok) {
			// Only store file_id if Google Drive upload was performed (for Google Docs API backward compatibility)
			// DOCX templating doesn't need Google Drive upload, so file_id may be null
			if (result.file_id) {
				store.user.imgUploadId = result.file_id;
			}
			store.overlays.loading = false;
		} else {
			store.overlays.loading = false;
			console.error(
				"Error saving room image: ",
				result.message || result.error,
			);
		}
	} catch (error) {
		store.overlays.loading = false;
		console.error("Error saving room image: ", error);
	} finally {
		// Reset export mode - canvas will be redrawn by normal flow if needed
		isExporting.value = false;
	}
};

// Get primary product (first product in array) for display
const primaryProduct = computed(() => {
	const product = store.getPrimaryProduct;
	if (!product || !product.data) return null;

	// Resolve size and variant data from product catalog
	const sizeData = product.data.sizes?.items?.[product.size];
	const variantData = product.size_variant
		? product.data.sizes?.variants?.all?.[product.size_variant]
		: null;

	return {
		...product,
		sizeLabel: sizeData?.l || "",
		variantLabel: variantData?.l || "",
		productName: product.data?.name || "",
	};
});

// Expose productTooBigForRoom to parent component
defineExpose({
	productTooBigForRoom,
});
</script>

<template>
	<section class="lx-report-section">
		<div class="lx-report-section-header">
			<h3 class="lux-copy-large lux-copy-semibold uppercase">Room Mock-Up</h3>
		</div>
		<div v-if="!readyToCalc" class="mt-1 text-sm">
			Waiting for user input.
			<div
				v-if="store.validation.Product.showErrors"
				class="mt-1 text-sm text-red-600"
			>
				Please complete all product size selections above.
			</div>
		</div>
		<div class="relative min-h-[400px]" v-if="readyToCalc">
			<div
				class="absolute bottom-0 left-0 right-0 top-0 z-50 bg-blacka75"
				v-show="isLoading"
			></div>

			<div v-if="store.hasCustomProductSelections">
				<p class="mb-4">
					You have created a custom configuration of Luxxbox acoustic lighting
					and baffling products. They combine to achieve a reverberation time of
					<strong>{{ store.resultingT60 }}</strong> seconds,
					<strong
						><span v-if="store.totalSabins > 0">
							{{ store.totalSabins }} Metric Sabins ({{
								Utils.convertMetricToImperialSabins(store.totalSabins)
							}}
							Imperial Sabins)</span
						></strong
					>, and a total lumen output of
					<strong>{{ store.totalLumens }}lm</strong>.
				</p>
			</div>
			<div v-else>
				<p class="mb-4">
					To achieve the target room reverberation time of
					<strong>{{ store.user.room.usagetarget }}</strong> seconds, for
					<strong>{{ store.user.room.usage }}</strong> we recommend
					<strong>
						<span v-if="primaryProduct?.calculation?.unitsUsed"
							>{{ primaryProduct.calculation.unitsUsed }} x </span
						>{{ primaryProduct?.productName }} ({{ primaryProduct?.sizeLabel }}
						<span v-if="primaryProduct?.size_variant"
							>&mdash; {{ primaryProduct?.variantLabel }} </span
						>)</strong
					>. Detailed acoustic values are included in the generated report.
				</p>
			</div>

			<p class="mb-4">
				Not intended to provide optimal layout for lighting levels, ideally use
				only enough lit elements to achieve lighting outcomes and add equivalent
				acoustic baffle versions to achieve acoustic outcomes.
			</p>
			<!-- Lumen Output Table -->

			<div
				id="canvas-container"
				ref="canvasContainer"
				class="w-full bg-gray-100 p-0 text-center [&_canvas]:max-w-full [&_canvas]:h-auto"
			>
				<canvas ref="canvas"></canvas>
			</div>

			<!-- Interactive HTML Legend (click to show/hide products) -->
			<p
				v-if="productColorLegend.length > 0"
				class="m-0 pt-3 px-4 pb-0 text-sm text-gray-500 bg-gray-100"
			>
				Click/tap the items to toggle product images:
				<a
					v-if="!allProductsVisible"
					href="#"
					class="text-blue-600 hover:text-blue-800 hover:underline"
					@click.prevent="showAllProducts"
					title="Show all products."
					>Show all.</a
				>
			</p>
			<div
				v-if="productColorLegend.length > 0"
				class="flex flex-wrap gap-2 p-4 bg-gray-100 justify-start"
			>
				<button
					v-for="item in productColorLegend"
					:key="item.productId"
					type="button"
					class="group inline-flex items-center gap-2 py-1.5 px-3 bg-white border border-black/15 cursor-pointer transition-all duration-200 text-xs text-gray-700 hover:border-black/40 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-100 disabled:hover:border-gray-200 disabled:hover:bg-white"
					:class="{
						'opacity-50 line-through': !item.visible,
					}"
					:disabled="item.visible && !canToggleOff(item.productId)"
					@click="toggleProductVisibility(item.productId)"
					:title="
						item.visible && !canToggleOff(item.productId)
							? 'At least one product must be visible'
							: item.visible
								? 'Click to hide'
								: 'Click to show'
					"
				>
					<span
						class="w-4 h-4 border border-black/15 shrink-0"
						:class="{ 'opacity-40': !item.visible }"
						:style="{ backgroundColor: item.color }"
					></span>
					<span class="whitespace-nowrap">
						{{ item.productName }} ({{ item.sizeLabel
						}}<span v-if="item.variantLabel"> — {{ item.variantLabel }}</span
						><span v-if="item.isNonLit"> — non-lit</span
						><span v-else-if="item.lumenOutput"> — {{ item.lumenOutput }}</span
						>)
					</span>
				</button>
			</div>
		</div>
		<!-- <div class="border border-red-700 p-5 text-red-700">
            <strong>Raw Calc Data</strong>
            <div
                class="text-sm"
                v-for="(value, key) in store.calcresult"
                :id="key">
                {{ key }}: {{ value }}
            </div>
        </div> -->
		<!-- <button
            type="button"
            @click="exportCanvasAsImage"
            >Save as Image</button
        > -->
	</section>
</template>
